import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Auth Pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";

// Driver Pages
import DriverDashboard from "@/pages/driver/Dashboard";
import DriverStatements from "@/pages/driver/Statements";
import DriverProfile from "@/pages/driver/Profile";
import DriverContracts from "@/pages/driver/Contracts";
import DriverShift from "@/pages/driver/Shift";
import ContractSign from "@/pages/driver/ContractSign";

// Admin Pages
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminDrivers from "@/pages/admin/Drivers";
import AdminStatements from "@/pages/admin/Statements";
import AdminRegistrations from "@/pages/admin/Registrations";
import AdminExports from "@/pages/admin/Exports";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminTenders from "@/pages/admin/Tenders";
import AdminCredentials from "@/pages/admin/Credentials";
import AdminEmployees from "@/pages/admin/Employees";
import AdminPermissions from "@/pages/admin/AdminPermissions";
import UserManager from "@/pages/admin/UserManager";
import JobCards from "@/pages/admin/JobCards";
import GenerateStatements from "@/pages/admin/GenerateStatements";
import DeductionsLedger from "@/pages/admin/DeductionsLedger";
import Sites from "@/pages/admin/Sites";
import StatementHistory from "@/pages/admin/StatementHistory";
import FinanceDashboard from "@/pages/admin/FinanceDashboard";
import Income from "@/pages/admin/Income";
import Expenses from "@/pages/admin/Expenses";
import Forecast from "@/pages/admin/Forecast";
import CashFlow from "@/pages/admin/CashFlow";
import FinancialYear from "@/pages/admin/FinancialYear";
import BankingImport from "@/pages/admin/BankingImport";
import BankingTransactions from "@/pages/admin/BankingTransactions";
import BankingRules from "@/pages/admin/BankingRules";
import BankingUncategorized from "@/pages/admin/BankingUncategorized";
import Categories from "@/pages/admin/Categories";
import GLCodes from "@/pages/admin/GLCodes";
import VATSummary from "@/pages/admin/VATSummary";
// removed: AIAdvisor
import OwnerDriverPayouts from "@/pages/admin/OwnerDriverPayouts";
import FuelTracker from "@/pages/admin/FuelTracker";
import TransportRevenue from "@/pages/admin/TransportRevenue";
import TransportExpenses from "@/pages/admin/TransportExpenses";
import TransportPnL from "@/pages/admin/TransportPnL";
import GroupOverview from "@/pages/admin/GroupOverview";
import FinancialAdvisor from "@/pages/admin/FinancialAdvisor";
import Clerks from "@/pages/admin/Clerks";
import Backups from "@/pages/admin/Backups";
import PartsCatalogPage from "@/pages/auto-spares/PartsCatalogPage";
import SalesPriceGuide from "@/pages/auto-spares/SalesPriceGuide";
import StockTakePage from "@/pages/auto-spares/StockTakePage";
import HrDashboard from "@/pages/hr/HrDashboard";
import HrCreate from "@/pages/hr/HrCreate";
import HrDetail from "@/pages/hr/HrDetail";
import PricingMonitor from "@/pages/admin/PricingMonitor";
import LoansTracker from "@/pages/admin/LoansTracker";
import ClerkTasksAdmin from "@/pages/admin/ClerkTasksAdmin";
import FleetMaintenance from "@/pages/fleet/FleetMaintenance";
import ClerkPerformance from "@/pages/admin/ClerkPerformance";
import InternalStaffAdmin from "@/pages/admin/InternalStaffAdmin";
import DriverRevenue from "@/pages/admin/DriverRevenue";
import AllPayslips from "@/pages/admin/AllPayslips";
import MenuSettings from "@/pages/admin/MenuSettings";
import StaffRegister from "@/pages/admin/StaffRegister";
import LeaveManager from "@/pages/admin/LeaveManager";
import IncentiveSettings from "@/pages/admin/IncentiveSettings";
import StaffLoansAdmin from "@/pages/admin/StaffLoansAdmin";
import FleetTrips from "@/pages/admin/FleetTrips";
import FleetTripsDriverDetail from "@/pages/admin/FleetTripsDriverDetail";
import DriverShiftsAdmin from "@/pages/admin/DriverShiftsAdmin";
import DailyAttendance from "@/pages/admin/DailyAttendance";
import VehicleChangeRequestsAdmin from "@/pages/admin/VehicleChangeRequestsAdmin";
import CompanyRegister from "@/pages/admin/CompanyRegister";
import DriverPnL from "@/pages/admin/DriverPnL";
import Applications from "@/pages/admin/Applications";
import SalesAnalytics from "@/pages/admin/SalesAnalytics";
import VettingList from "@/pages/vetting/VettingList";
import VettingCase from "@/pages/vetting/VettingCase";
import StaffDashboard from "@/pages/StaffDashboard";
import ImpersonatePage from "@/pages/admin/ImpersonatePage";

// Auto Spares Module
import AutoSparesSalesmanDashboard from "@/pages/admin/AutoSpares/SalesmanDashboard";
import AutoSparesAdminDashboard from "@/pages/admin/AutoSpares/AdminDashboard";
import ClerkSalesRingUp from "@/pages/admin/AutoSpares/ClerkSalesRingUp";

// Procurement
import ProcurementPage from "@/pages/procurement/ProcurementPage";

// Public Pages
import NewDriverRegistration from "@/pages/public/NewDriverRegistration";

import "@/App.css";

// Helper: find the first route a clerk is permitted to see (fallback landing)
const clerkLandingRoute = (user) => {
  const p = user?.permissions || {};
  if (p.can_view_dashboard) return '/admin';
  if (p.can_view_group_overview) return '/admin/group-overview';
  if (p.can_manage_drivers) return '/admin/drivers';
  if (p.can_view_credentials) return '/admin/credentials';
  if (p.can_manage_employees) return '/admin/employees';
  if (p.can_manage_clerks) return '/admin/clerks';
  if (p.can_manage_registrations) return '/admin/registrations';
  if (p.can_view_statements) return '/admin/statements/generate';
  if (p.can_view_transport_pnl) return '/admin/finance/transport-pnl';
  if (p.can_manage_transport_revenue) return '/admin/finance/transport-revenue';
  if (p.can_manage_transport_expenses) return '/admin/finance/transport-expenses';
  if (p.can_manage_fuel_tracker) return '/admin/fuel-tracker';
  if (p.can_view_audit_logs) return '/admin/audit-logs';
  if (p.can_manage_backups) return '/admin/backups';
  if (p.can_view_auto_spares) return '/auto-spares/parts-catalog';
  if (p.can_manage_loans_tracker) return '/loans-tracker';
  return '/admin/group-overview'; // fallback (will show access denied)
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'clerk') return <Navigate to={clerkLandingRoute(user)} replace />;
    if (user.role === 'auto_spares_salesman') return <Navigate to="/auto-spares" replace />;
    if (user.role === 'auto_spares_admin') return <Navigate to="/auto-spares/admin" replace />;
    if (user.role === 'auto_spares_super') return <Navigate to="/auto-spares/admin" replace />;
    if (user.role === 'internal_staff') return <Navigate to="/staff" replace />;
    return <Navigate to="/driver" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'clerk') return <Navigate to={clerkLandingRoute(user)} replace />;
    if (user.role === 'auto_spares_salesman') return <Navigate to="/auto-spares" replace />;
    if (user.role === 'auto_spares_admin') return <Navigate to="/auto-spares/admin" replace />;
    if (user.role === 'auto_spares_super') return <Navigate to="/auto-spares/admin" replace />;
    if (user.role === 'internal_staff') return <Navigate to="/staff" replace />;
    return <Navigate to="/driver" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/new-driver-registration" element={<NewDriverRegistration />} />
      <Route path="/impersonate" element={<ImpersonatePage />} />
      
      {/* Driver Routes */}
      <Route path="/driver" element={<ProtectedRoute allowedRoles={['driver']}><DriverDashboard /></ProtectedRoute>} />
      <Route path="/driver/statements" element={<ProtectedRoute allowedRoles={['driver']}><DriverStatements /></ProtectedRoute>} />
      <Route path="/driver/profile" element={<ProtectedRoute allowedRoles={['driver']}><DriverProfile /></ProtectedRoute>} />
      <Route path="/driver/contracts" element={<ProtectedRoute allowedRoles={['driver']}><DriverContracts /></ProtectedRoute>} />
      <Route path="/driver/shift" element={<ProtectedRoute allowedRoles={['driver']}><DriverShift /></ProtectedRoute>} />
      <Route path="/driver/contracts/:contractId/sign" element={<ProtectedRoute allowedRoles={['driver']}><ContractSign /></ProtectedRoute>} />
      
      {/* Admin Contract View (same component, admin can view any contract) */}
      <Route path="/admin/contracts/:contractId" element={<ProtectedRoute allowedRoles={['admin']}><ContractSign /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/drivers" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminDrivers /></ProtectedRoute>} />
      <Route path="/admin/statements" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminStatements /></ProtectedRoute>} />
      <Route path="/admin/statements/generate" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><GenerateStatements /></ProtectedRoute>} />
      <Route path="/admin/sites" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><Sites /></ProtectedRoute>} />
      <Route path="/admin/statements/history" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><StatementHistory /></ProtectedRoute>} />
      <Route path="/admin/deductions-ledger" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><DeductionsLedger /></ProtectedRoute>} />
      <Route path="/admin/registrations" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminRegistrations /></ProtectedRoute>} />
      <Route path="/admin/credentials" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminCredentials /></ProtectedRoute>} />
      <Route path="/admin/employees" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminEmployees /></ProtectedRoute>} />
      <Route path="/admin/finance/dashboard" element={<ProtectedRoute allowedRoles={['admin']}><FinanceDashboard /></ProtectedRoute>} />
      <Route path="/admin/finance/income" element={<ProtectedRoute allowedRoles={['admin']}><Income /></ProtectedRoute>} />
      <Route path="/admin/finance/expenses" element={<ProtectedRoute allowedRoles={['admin']}><Expenses /></ProtectedRoute>} />
      <Route path="/admin/finance/forecast" element={<ProtectedRoute allowedRoles={['admin']}><Forecast /></ProtectedRoute>} />
      <Route path="/admin/finance/cashflow" element={<ProtectedRoute allowedRoles={['admin']}><CashFlow /></ProtectedRoute>} />
      <Route path="/admin/finance/year" element={<ProtectedRoute allowedRoles={['admin']}><FinancialYear /></ProtectedRoute>} />
      <Route path="/admin/banking/import" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><BankingImport /></ProtectedRoute>} />
      <Route path="/admin/banking/transactions" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><BankingTransactions /></ProtectedRoute>} />
      <Route path="/admin/banking/rules" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><BankingRules /></ProtectedRoute>} />
      <Route path="/admin/banking/uncategorized" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><BankingUncategorized /></ProtectedRoute>} />
      <Route path="/admin/finance/categories" element={<ProtectedRoute allowedRoles={['admin']}><Categories /></ProtectedRoute>} />
      <Route path="/admin/finance/gl-codes" element={<ProtectedRoute allowedRoles={['admin']}><GLCodes /></ProtectedRoute>} />
      <Route path="/admin/finance/vat" element={<ProtectedRoute allowedRoles={['admin']}><VATSummary /></ProtectedRoute>} />


      <Route path="/admin/finance/owner-driver-payouts" element={<ProtectedRoute allowedRoles={['admin']}><OwnerDriverPayouts /></ProtectedRoute>} />
      <Route path="/admin/finance/transport-revenue" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><TransportRevenue /></ProtectedRoute>} />
      <Route path="/admin/finance/transport-expenses" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><TransportExpenses /></ProtectedRoute>} />
      <Route path="/admin/finance/transport-pnl" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><TransportPnL /></ProtectedRoute>} />
      <Route path="/admin/financial-advisor" element={<ProtectedRoute allowedRoles={['admin']}><FinancialAdvisor /></ProtectedRoute>} />
      <Route path="/admin/group-overview" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><GroupOverview /></ProtectedRoute>} />
      <Route path="/admin/clerks" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><Clerks /></ProtectedRoute>} />
      <Route path="/admin/backups" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><Backups /></ProtectedRoute>} />
      <Route path="/admin/fuel-tracker" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><FuelTracker /></ProtectedRoute>} />
      <Route path="/admin/fleet-maintenance" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><FleetMaintenance /></ProtectedRoute>} />
      <Route path="/admin/permissions" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminPermissions /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><UserManager /></ProtectedRoute>} />
      <Route path="/admin/job-cards" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><JobCards /></ProtectedRoute>} />
      <Route path="/admin/exports" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminExports /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminAuditLogs /></ProtectedRoute>} />
      <Route path="/admin/tenders" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><AdminTenders /></ProtectedRoute>} />
      
      {/* Auto Spares Module Routes */}
      <Route path="/auto-spares" element={<ProtectedRoute allowedRoles={['auto_spares_salesman', 'auto_spares_admin', 'admin']}><AutoSparesSalesmanDashboard /></ProtectedRoute>} />
      <Route path="/auto-spares/admin" element={<ProtectedRoute allowedRoles={['auto_spares_admin', 'auto_spares_super', 'admin']}><AutoSparesAdminDashboard /></ProtectedRoute>} />
      <Route path="/auto-spares/parts-catalog" element={<ProtectedRoute allowedRoles={['admin', 'clerk', 'auto_spares_admin', 'auto_spares_super', 'auto_spares_salesman']}><PartsCatalogPage /></ProtectedRoute>} />
      <Route path="/auto-spares/price-guide" element={<ProtectedRoute allowedRoles={['admin', 'clerk', 'auto_spares_admin', 'auto_spares_super', 'auto_spares_salesman']}><SalesPriceGuide /></ProtectedRoute>} />
      <Route path="/auto-spares/stock-take" element={<ProtectedRoute allowedRoles={['admin', 'clerk', 'auto_spares_admin', 'auto_spares_super']}><StockTakePage /></ProtectedRoute>} />
      <Route path="/auto-spares/clerk-ringup" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><ClerkSalesRingUp /></ProtectedRoute>} />
      <Route path="/hr" element={<ProtectedRoute allowedRoles={['admin', 'hr_admin']}><HrDashboard /></ProtectedRoute>} />
      <Route path="/hr/new" element={<ProtectedRoute allowedRoles={['admin', 'hr_admin']}><HrCreate /></ProtectedRoute>} />
      <Route path="/hr/:id" element={<ProtectedRoute allowedRoles={['admin', 'hr_admin']}><HrDetail /></ProtectedRoute>} />
      <Route path="/admin/pricing-monitor" element={<ProtectedRoute allowedRoles={['admin', 'auto_spares_admin', 'auto_spares_super']}><PricingMonitor /></ProtectedRoute>} />
      <Route path="/loans-tracker" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><LoansTracker /></ProtectedRoute>} />
      <Route path="/vetting" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><VettingList /></ProtectedRoute>} />
      <Route path="/vetting/:id" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><VettingCase /></ProtectedRoute>} />
      <Route path="/procurement" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><ProcurementPage /></ProtectedRoute>} />
      <Route path="/admin/tasks" element={<ProtectedRoute allowedRoles={['admin']}><ClerkTasksAdmin /></ProtectedRoute>} />
      <Route path="/admin/performance" element={<ProtectedRoute allowedRoles={['admin']}><ClerkPerformance /></ProtectedRoute>} />
      <Route path="/admin/internal-staff" element={<ProtectedRoute allowedRoles={['admin']}><InternalStaffAdmin /></ProtectedRoute>} />
      <Route path="/admin/driver-revenue" element={<ProtectedRoute allowedRoles={['admin']}><DriverRevenue /></ProtectedRoute>} />
      <Route path="/admin/staff-register" element={<ProtectedRoute allowedRoles={['admin']}><StaffRegister /></ProtectedRoute>} />
      <Route path="/admin/leave" element={<ProtectedRoute allowedRoles={['admin']}><LeaveManager /></ProtectedRoute>} />
      <Route path="/admin/incentive-settings" element={<ProtectedRoute allowedRoles={['admin']}><IncentiveSettings /></ProtectedRoute>} />
      <Route path="/admin/staff-loans" element={<ProtectedRoute allowedRoles={['admin']}><StaffLoansAdmin /></ProtectedRoute>} />
      <Route path="/admin/fleet-trips" element={<ProtectedRoute allowedRoles={['admin']}><FleetTrips /></ProtectedRoute>} />
      <Route path="/admin/fleet-trips/driver/:driverId" element={<ProtectedRoute allowedRoles={['admin']}><FleetTripsDriverDetail /></ProtectedRoute>} />
      <Route path="/admin/driver-shifts" element={<ProtectedRoute allowedRoles={['admin']}><DriverShiftsAdmin /></ProtectedRoute>} />
      <Route path="/admin/daily-attendance" element={<ProtectedRoute allowedRoles={['admin']}><DailyAttendance /></ProtectedRoute>} />
      <Route path="/admin/vehicle-change-requests" element={<ProtectedRoute allowedRoles={['admin']}><VehicleChangeRequestsAdmin /></ProtectedRoute>} />
      <Route path="/admin/company-register" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><CompanyRegister /></ProtectedRoute>} />
      <Route path="/admin/finance/driver-pnl" element={<ProtectedRoute allowedRoles={['admin']}><DriverPnL /></ProtectedRoute>} />
      <Route path="/admin/applications" element={<ProtectedRoute allowedRoles={['admin', 'clerk']}><Applications /></ProtectedRoute>} />
      <Route path="/admin/sales-analytics" element={<ProtectedRoute allowedRoles={['admin']}><SalesAnalytics /></ProtectedRoute>} />
      <Route path="/admin/all-payslips" element={<ProtectedRoute allowedRoles={['admin']}><AllPayslips /></ProtectedRoute>} />
      <Route path="/admin/menu-settings" element={<ProtectedRoute allowedRoles={['admin']}><MenuSettings /></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute allowedRoles={['internal_staff']}><StaffDashboard /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
